/**
 * Capacitor adapter for the shared transport-neutral Marvin conversation runtime.
 * Swift owns visible UI and the session lease. This file owns bridge admission,
 * native offline cache integration, attachment upload, the cards' commands
 * (`engineCardCommands.ts`), and byte-compatible reports only.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

import { ConversationApi } from "../api/conversation";
import { FileApi } from "../api/file";
import { RunApi } from "../api/run";
import type { ApiAnsweredQuestion, ApiCreateRunRequest, ApiStreamEvent } from "../api/run";
import type { ApiReference } from "../shared/interfaces";
import { THINKING_MESSAGES } from "../shared/constants";
import { ProjectSettingsApi } from "../api/projectSettings";
import { FoldersApi } from "../api/folders";
import type { PrefsCapability } from "../platform/Platform";
import { getPlatform } from "../platform/platformRef";
import {
    folderToken,
    isFolderToken,
    parseSourceTokens,
    SourceToken,
} from "../shared/marvinSources";
import { getDocumentFiltersForSources } from "../utils/marvinSourceFilters";
import {
    liveDefaultScopes,
    MARVIN_DEFAULT_SCOPE_KEY,
    NATIVE_SCOPED_CREATE_FLAG_KEY,
    resolveMarvinDefaultScopes,
} from "../shared/marvinDefaultScopes";
import { createConversationRuntime, toEngineConversation } from "../marvin/conversationRuntime";
import type {
    ConversationRuntime,
    ConversationRuntimeEvent,
    EngineConversation,
    EngineConversationSummary,
    EngineStreamSnapshot,
} from "../marvin/conversationRuntime";
import {
    decideAction,
    decideMarvinShapes,
    isMarkupCommand,
    readMarvinShapes,
    type EngineMarkupCommand,
} from "./engineCardCommands";
import { ensureReferencesResolved, resolvedFiles, resolvedSections } from "./referenceResolution";
function prefs(): PrefsCapability {
    return getPlatform().prefs;
}

export type {
    EngineConversation,
    EngineConversationSummary,
    EngineMessage,
    EngineSegment,
    EngineStreamSnapshot,
    EngineThinking,
    NativeRunStreamState,
} from "../marvin/conversationRuntime";
export {
    createNativeRunStreamState,
    nativeStreamMarkdown,
    reduceNativeRunStreamState,
} from "../marvin/conversationRuntime";

/** A file native staged for the question (a photo, a PDF, any file), as a temporary-file path. */
export interface EngineCommandPhoto {
    path: string;
    name: string;
    mimeType: string;
}

interface EngineSessionLease {
    readonly instanceId: string;
    readonly sessionGeneration: number;
}

interface EngineCacheContext {
    readonly keyPrefix: string | null;
    readonly storeGeneration: number;
}

interface EngineCommand extends EngineSessionLease {
    id: string;
    type: string;
    conversationId?: string;
    text?: string;
    photos?: EngineCommandPhoto[];
    /** Marvin source tokens picked natively. Missing or empty means the
     * tenant default, resolved inside this JS engine. */
    sources?: string[];
    /** `attach`: the server run a previous engine page was streaming. */
    runId?: string;
    /** `loadConversation`: skip the cached copy and report only the server's. */
    refresh?: boolean;
    /** `send` / `createAndRun`: "deep" asks for a Deep research turn. */
    depth?: string;
    /** `send`: the question card the message answers (a native card's pick). */
    answersQuestion?: unknown;
    /** `decideAction`: the action and the decision (`runId` is the run's). */
    actionId?: string;
    approve?: boolean;
    /** `readMarvinShapes` / `decideMarvinShapes`: the markup card's draft, shapes and decision. */
    markup?: EngineMarkupCommand;
}

interface ConversationEnginePlugin {
    reportReady(options: {
        instanceId: string;
        admissionToken?: string;
    }): Promise<EngineSessionLease>;
    reportConversations(
        options: EngineSessionLease & { conversations: EngineConversationSummary[] },
    ): Promise<void>;
    /** The conversation as one JSON text: WebKit hands native a string in one copy, where an
     * object's whole tree became Foundation objects on the main thread (`EngineBridgeJSON.swift`). */
    reportConversation(options: EngineSessionLease & { conversationJson: string }): Promise<void>;
    /** The snapshot as one JSON text, as `reportConversation`'s. */
    reportStream(options: EngineSessionLease & { snapshotJson: string }): Promise<void>;
    reportCommandTerminal(options: EngineSessionLease & { commandId: string }): Promise<void>;
    reportFailure(
        options: EngineSessionLease & { commandId: string; message: string },
    ): Promise<void>;
    reportOfflineCacheComplete(options: EngineSessionLease & { commandId: string }): Promise<void>;
    /** A card command's answer (`decideAction`, `readMarvinShapes`, `decideMarvinShapes`). */
    reportCommandResult(
        options: EngineSessionLease & { commandId: string; result: unknown },
    ): Promise<void>;
    addListener(
        eventName: "command",
        listenerFunc: (command: EngineCommand) => void,
    ): Promise<PluginListenerHandle>;
}

const plugin = registerPlugin<ConversationEnginePlugin>("ConversationEngine");

function createEngineInstanceId(): string {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (typeof globalThis.crypto?.getRandomValues === "function") {
        globalThis.crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
        .slice(6, 8)
        .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

/** One UUID for this JS realm. A full native auth/tenant reload creates a new one. */
let engineInstanceId = createEngineInstanceId();
let acceptedEngineLease: EngineSessionLease | null = null;
let readyHandshakePending = false;
const commandsAwaitingReady: EngineCommand[] = [];

function leased<T extends object>(lease: EngineSessionLease, payload: T): T & EngineSessionLease {
    return {
        ...payload,
        instanceId: lease.instanceId,
        sessionGeneration: lease.sessionGeneration,
    };
}

function acceptedCommandLease(command: EngineCommand): EngineSessionLease | null {
    const active = acceptedEngineLease;
    if (
        !active ||
        command.instanceId !== active.instanceId ||
        command.sessionGeneration !== active.sessionGeneration
    ) {
        return null;
    }
    return Object.freeze({
        instanceId: command.instanceId,
        sessionGeneration: command.sessionGeneration,
    });
}

/** The engine's cache names its reader (`prefs.tenantKey`). Copies earlier builds kept under
 *  the organization alone are never read: nothing says whose they were. */
function captureEngineCacheContext(): EngineCacheContext {
    const store = prefs();
    return Object.freeze({
        keyPrefix: store.tenantKey("conversationEngine", ""),
        storeGeneration: store.captureGeneration(),
    });
}

function sameEngineCacheContext(a: EngineCacheContext, b: EngineCacheContext): boolean {
    return a.keyPrefix === b.keyPrefix && a.storeGeneration === b.storeGeneration;
}

function engineCacheKey(context: EngineCacheContext, suffix: string): string | null {
    return context.keyPrefix === null ? null : `${context.keyPrefix}${suffix}`;
}

async function readEngineCache<T>(context: EngineCacheContext, suffix: string): Promise<T | null> {
    const key = engineCacheKey(context, suffix);
    if (!key || !prefs().isGenerationCurrent(context.storeGeneration)) return null;
    const stored = await prefs().read<T>(key, context.storeGeneration);
    return prefs().isGenerationCurrent(context.storeGeneration) ? stored : null;
}

async function writeEngineCache(
    context: EngineCacheContext,
    suffix: string,
    value: unknown,
): Promise<void> {
    const key = engineCacheKey(context, suffix);
    if (!key || !prefs().isGenerationCurrent(context.storeGeneration)) return;
    await prefs().write(key, value, context.storeGeneration);
}

const runtimePorts = {
    listConversations: () => ConversationApi.getAllConversations(),
    getConversation: (id: string) => ConversationApi.getConversation(id),
    createConversation: (message: string, messageType: string, documentFilters?: string[]) =>
        ConversationApi.createConversation(message, messageType, documentFilters),
    createRun: (request: ApiCreateRunRequest) => RunApi.create(request),
    streamRun: (
        runId: string,
        onEvent: (event: ApiStreamEvent) => void,
        options?: { afterSeq?: number },
    ) => RunApi.stream(runId, onEvent, options),
    cancelRun: (runId: string) => RunApi.cancel(runId),
    decideAction: (runId: string, actionId: string, approve: boolean) =>
        RunApi.decideAction(runId, actionId, approve),
};

const referenceResolver = {
    resolve: (references: ApiReference[]) => ensureReferencesResolved(references),
    getSectionTarget: (sectionId: string) => resolvedSections.get(sectionId),
    getFileTarget: (fileId: string) => resolvedFiles.get(fileId),
};

interface AdapterRuntime {
    runtime: ConversationRuntime;
    unsubscribe: () => void;
    /** Reports a copy held back while its citations resolved, if the resolved one never came. */
    flush: () => void;
}

interface AdapterRuntimeOptions {
    reportData?: boolean;
    reportSnapshots?: boolean;
    suppressFailures?: boolean;
    onSnapshot?: (snapshot: EngineStreamSnapshot) => void;
    /** The copy of the conversation native shows already (the cached one just reported), and its JSON. */
    shown?: { conversation: EngineConversation; json: string } | null;
}

/** A summary with its last activity in ms since 1970: native sorts the list without parsing dates. */
function withActivityTime(summary: EngineConversationSummary): EngineConversationSummary {
    const lastActivityAtMs = Date.parse(summary.lastActivityAt);
    return Number.isFinite(lastActivityAtMs) ? { ...summary, lastActivityAtMs } : summary;
}

/** What a reported copy holds, to tell whether another copy adds anything to it. */
interface ShownConversation {
    json: string;
    /** Its messages and their states: a copy whose differ brings news native should show at once. */
    outline: string;
}

function shownConversation(conversation: EngineConversation, json?: string): ShownConversation {
    return {
        json: json ?? JSON.stringify(conversation),
        outline: conversation.messages
            .map((message) => `${message.id}:${message.type}:${message.status ?? ""}`)
            .join("\n"),
    };
}

function createAdapterRuntime(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    options: AdapterRuntimeOptions = {},
): AdapterRuntime {
    const runtime = createConversationRuntime(runtimePorts, {
        fallbackStatusText: () =>
            THINKING_MESSAGES[Math.floor(Math.random() * THINKING_MESSAGES.length)],
        referenceResolver,
        // The engine ships inside the app with the native build that reads it: every reader has
        // the turn, so its legacy copy would only double the bridge's bytes.
        omitLegacyBesideTurn: true,
    });
    // Each copy reported by this command, by conversation: the same copy again (a server copy
    // equal to the cached one, a resolution that changed no link) is not sent again, so native
    // neither deserializes nor redraws a conversation that did not change.
    const shown = new Map<string, ShownConversation>();
    if (options.shown) {
        shown.set(
            options.shown.conversation.id,
            shownConversation(options.shown.conversation, options.shown.json),
        );
    }
    let held: EngineConversation | null = null;
    const reportConversation = (conversation: EngineConversation, json?: string) => {
        const reported = shownConversation(conversation, json);
        shown.set(conversation.id, reported);
        void plugin.reportConversation(leased(lease, { conversationJson: reported.json }));
        void writeEngineCache(cacheContext, `conversation:${conversation.id}`, conversation);
    };
    const flush = () => {
        const pending = held;
        held = null;
        if (pending) reportConversation(pending);
    };
    const unsubscribe = runtime.subscribeEvents((event: ConversationRuntimeEvent) => {
        if (event.type === "failure") {
            if (!options.suppressFailures) {
                void plugin.reportFailure(
                    leased(lease, { commandId: event.commandId, message: event.message }),
                );
            }
            return;
        }
        if (event.type === "snapshot") {
            if (options.reportSnapshots) {
                void plugin.reportStream(
                    leased(lease, { snapshotJson: JSON.stringify(event.snapshot) }),
                );
                options.onSnapshot?.(event.snapshot);
            }
            return;
        }
        if (!options.reportData) return;
        if (event.type === "conversations") {
            const conversations = event.conversations.map(withActivityTime);
            void plugin.reportConversations(leased(lease, { conversations }));
            void writeEngineCache(cacheContext, "list", conversations);
            return;
        }
        const conversation = toEngineConversation(event.conversation);
        const previous = shown.get(conversation.id);
        const next = shownConversation(conversation);
        // Native already shows this conversation with the same messages; only citation targets can
        // differ, and the resolved copy follows: send that one alone (`flush` if it never comes).
        if (event.provisional && previous?.outline === next.outline) {
            held = conversation;
            return;
        }
        held = null;
        if (previous?.json === next.json) return;
        reportConversation(conversation, next.json);
    });
    return { runtime, unsubscribe, flush };
}

interface ActiveAdapterRun extends AdapterRuntime {
    lease: EngineSessionLease;
    cacheContext: EngineCacheContext;
    commandId: string;
    conversationId: string;
    settled: boolean;
}

const activeRuns = new Map<string, ActiveAdapterRun>();

function runForConversation(conversationId: string): ActiveAdapterRun | null {
    return (
        Array.from(activeRuns.values()).find((run) => run.conversationId === conversationId) ?? null
    );
}

function retireSupersededRun(run: ActiveAdapterRun | null): void {
    if (!run) return;
    activeRuns.delete(run.commandId);
    run.unsubscribe();
    run.runtime.dispose();
    void plugin.reportCommandTerminal(leased(run.lease, { commandId: run.commandId }));
}

function beginAdapterRun(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
    conversationId: string,
): ActiveAdapterRun {
    const adapter = createAdapterRuntime(lease, cacheContext, {
        reportSnapshots: true,
        onSnapshot: (snapshot) => {
            const active = activeRuns.get(commandId);
            if (!active) return;
            if (snapshot.conversationId) active.conversationId = snapshot.conversationId;
            if (
                !active.settled &&
                snapshot.status !== "creating" &&
                snapshot.status !== "streaming"
            ) {
                active.settled = true;
                activeRuns.delete(commandId);
                active.unsubscribe();
                void refreshConversationCacheAfterRun(
                    active.lease,
                    active.cacheContext,
                    snapshot.conversationId,
                );
            }
        },
    });
    const active: ActiveAdapterRun = {
        ...adapter,
        lease,
        cacheContext,
        commandId,
        conversationId,
        settled: false,
    };
    activeRuns.set(commandId, active);
    return active;
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : "Something went wrong";
}

function reportCommandFailure(lease: EngineSessionLease, commandId: string, error: unknown): void {
    void plugin.reportFailure(leased(lease, { commandId, message: errorMessage(error) }));
}

/** Runs a card command and reports its answer, or its failure, to the command that asked. */
async function reportCommandResult(
    lease: EngineSessionLease,
    commandId: string,
    work: () => Promise<unknown>,
): Promise<void> {
    try {
        const result = await work();
        await plugin.reportCommandResult(leased(lease, { commandId, result }));
    } catch (error) {
        reportCommandFailure(lease, commandId, error);
    }
}

/** Only "deep" is a depth; anything else asks for an ordinary turn. */
function depthOf(command: EngineCommand): "deep" | undefined {
    return command.depth === "deep" ? "deep" : undefined;
}

/** The card a `send` answers, when the command names one whole; anything else sends a plain message. */
export function answeredQuestionOf(answer: unknown): ApiAnsweredQuestion | undefined {
    if (typeof answer !== "object" || answer === null) return undefined;
    const { messageId, questionId, answers, otherText } = answer as Record<string, unknown>;
    if (
        typeof messageId !== "string" ||
        !messageId ||
        typeof questionId !== "string" ||
        !questionId
    ) {
        return undefined;
    }
    if (!Array.isArray(answers) || !answers.every((item) => typeof item === "string"))
        return undefined;
    return {
        messageId,
        questionId,
        answers,
        otherText: typeof otherText === "string" && otherText ? otherText : null,
    };
}

/**
 * Uploads the question's files one at a time, in order. Each is read from the device into the
 * web view's memory to upload; read all at once, twenty 100 MB files would hold 2 GB there.
 */
export async function uploadCommandPhotos(photos: EngineCommandPhoto[]): Promise<string[]> {
    const ids: string[] = [];
    for (const photo of photos) {
        const response = await fetch(Capacitor.convertFileSrc(photo.path));
        const blob = await response.blob();
        const file = new File([blob], photo.name, { type: photo.mimeType });
        ids.push((await FileApi.uploadFile(file)).id);
    }
    return ids;
}

async function handleSend(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
    conversationId: string,
    text: string,
    photos?: EngineCommandPhoto[],
    depth?: "deep",
    answersQuestion?: ApiAnsweredQuestion,
): Promise<void> {
    retireSupersededRun(runForConversation(conversationId));
    const active = beginAdapterRun(lease, cacheContext, commandId, conversationId);
    try {
        await active.runtime.send({
            commandId,
            conversationId,
            text,
            ...(depth ? { depth } : {}),
            ...(answersQuestion ? { answersQuestion } : {}),
            prepareFiles: photos?.length ? () => uploadCommandPhotos(photos) : undefined,
        });
    } catch (error) {
        if (activeRuns.get(commandId) !== active) return;
        activeRuns.delete(commandId);
        reportCommandFailure(lease, commandId, error);
    }
}

async function handleCreateAndRun(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
    text: string,
    photos?: EngineCommandPhoto[],
    sources?: string[],
    depth?: "deep",
): Promise<void> {
    const active = beginAdapterRun(lease, cacheContext, commandId, "");
    try {
        await active.runtime.createAndRun({
            commandId,
            text,
            ...(depth ? { depth } : {}),
            prepareDocumentFilters: () => resolveDocumentFilters(cacheContext, sources),
            prepareFiles: photos?.length ? () => uploadCommandPhotos(photos) : undefined,
        });
    } catch (error) {
        if (activeRuns.get(commandId) !== active) return;
        activeRuns.delete(commandId);
        reportCommandFailure(lease, commandId, error);
    }
}

/** Match web Home: expand an explicit source selection, or resolve the
 * tenant's Marvin default. The server stores a missing filter list as
 * unscoped, so the native engine must always send the resolved list. */
async function resolveDocumentFilters(
    cacheContext: EngineCacheContext,
    sources: string[] | undefined,
): Promise<string[] | undefined> {
    const explicit = parseSourceTokens(sources);
    // A picked folder needs the live folder list whatever the settings say: ask for both at once.
    const folders = explicit.some(isFolderToken) ? liveFolderTokens() : null;
    // Fail closed: without the server-controlled release flag, preserve the
    // pre-change write shape and omit documentFilters.
    const features = await featureSettings(cacheContext);
    if (features?.[NATIVE_SCOPED_CREATE_FLAG_KEY] !== true) return undefined;

    const selected =
        explicit.length > 0
            ? explicit
            : resolveMarvinDefaultScopes(features?.[MARVIN_DEFAULT_SCOPE_KEY]);
    return getDocumentFiltersForSources(await liveSources(selected, folders));
}

/** Kept this long, the tenant's `features.` settings answer a new question at once (and refresh behind it). */
const FEATURE_SETTINGS_FRESH_MS = 10 * 60 * 1000;

interface FeatureSettingsEntry {
    /** The tenant scope and store generation the settings belong to. */
    key: string;
    value?: Record<string, unknown>;
    at: number;
    refreshing: Promise<Record<string, unknown> | undefined> | null;
}

let featureSettingsEntry: FeatureSettingsEntry | null = null;

function featureSettingsKey(context: EngineCacheContext): string | null {
    return context.keyPrefix === null ? null : `${context.storeGeneration}:${context.keyPrefix}`;
}

/**
 * The tenant's `features.` settings: the last answer when it is fresh, refreshed in the background
 * (a new question never waits on that round trip), else the server's, awaited. A failed fetch
 * keeps the last answer; with none it is undefined, which fails closed. One tenant's settings
 * never answer for another: the entry is keyed by its scope and store generation.
 */
function featureSettings(
    context: EngineCacheContext,
): Promise<Record<string, unknown> | undefined> {
    const key = featureSettingsKey(context);
    if (key === null) {
        return ProjectSettingsApi.getAllSettings("features.").catch(() => undefined);
    }
    if (featureSettingsEntry?.key !== key) {
        featureSettingsEntry = { key, at: 0, refreshing: null };
    }
    const entry = featureSettingsEntry;
    const refresh = (entry.refreshing ??= ProjectSettingsApi.getAllSettings("features.")
        .then(
            (value) => {
                entry.value = value;
                entry.at = Date.now();
                return value;
            },
            () => entry.value,
        )
        .finally(() => {
            entry.refreshing = null;
        }));
    return fresh(entry) ? Promise.resolve(entry.value) : refresh;
}

/** Fetches the tenant's `features.` settings now unless a fresh answer or a fetch is on hand. */
function warmFeatureSettings(context: EngineCacheContext): void {
    const key = featureSettingsKey(context);
    const entry = featureSettingsEntry;
    if (key === null || (entry?.key === key && (entry.refreshing || fresh(entry)))) return;
    void featureSettings(context);
}

function fresh(entry: FeatureSettingsEntry): boolean {
    return entry.value !== undefined && Date.now() - entry.at < FEATURE_SETTINGS_FRESH_MS;
}

/** The live folders as source tokens; null when the list could not be fetched. */
async function liveFolderTokens(): Promise<Array<{ token: SourceToken }> | null> {
    try {
        const response = await FoldersApi.list();
        return response.folders.map((folder) => ({ token: folderToken(folder.id) }));
    } catch {
        return null;
    }
}

async function liveSources(
    sources: SourceToken[],
    folders: Promise<Array<{ token: SourceToken }> | null> | null,
): Promise<SourceToken[]> {
    if (!sources.some(isFolderToken)) return sources;
    const live = await (folders ?? liveFolderTokens());
    // A failed folder fetch is not evidence that a folder was deleted.
    return live === null ? sources : liveDefaultScopes(sources, live);
}

function handleCancel(conversationId: string): void {
    const active = runForConversation(conversationId);
    if (!active) return;
    activeRuns.delete(active.commandId);
    active.settled = true;
    void active.runtime.cancel().finally(() => active.unsubscribe());
}

async function handleListConversations(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
): Promise<void> {
    const cached = await readEngineCache<EngineConversationSummary[]>(cacheContext, "list");
    if (cached) {
        void plugin.reportConversations(leased(lease, { conversations: cached }));
    }
    const adapter = createAdapterRuntime(lease, cacheContext, {
        reportData: true,
        suppressFailures: Boolean(cached),
    });
    await adapter.runtime.list(commandId);
    adapter.unsubscribe();
}

/** Re-attaches a run whose stream a previous engine page owned: native sends it for
 * every in-flight run after the WebView reloads (iOS can reload the hidden engine page
 * while the app is in the background). The command id is the original send's, so the
 * replayed snapshots land on the row that is waiting for them. The stream starts from
 * the run's first event, rebuilding the partial answer before tailing it live. */
function handleAttach(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
    conversationId: string,
    runId: string,
    userQuery: string,
): void {
    const existing = activeRuns.get(commandId);
    if (existing) {
        existing.runtime.resume();
        return;
    }
    retireSupersededRun(runForConversation(conversationId));
    const active = beginAdapterRun(lease, cacheContext, commandId, conversationId);
    active.runtime.attach({ commandId, conversationId, runId, userQuery });
}

async function handleLoadConversation(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
    conversationId: string,
    refresh = false,
): Promise<void> {
    // A refresh replaces a live row native settled from the server: an older cached copy
    // painted first would show the conversation without that answer for a moment.
    const cached = refresh
        ? null
        : await readEngineCache<EngineConversation>(cacheContext, `conversation:${conversationId}`);
    const shown = cached ? { conversation: cached, json: JSON.stringify(cached) } : null;
    if (shown) {
        void plugin.reportConversation(leased(lease, { conversationJson: shown.json }));
    }
    const adapter = createAdapterRuntime(lease, cacheContext, {
        reportData: true,
        suppressFailures: Boolean(cached),
        shown,
    });
    await adapter.runtime.load(commandId, conversationId);
    adapter.flush();
    adapter.unsubscribe();
}

const PREFETCH_CONVERSATION_CONCURRENCY = 4;
const prefetchInFlight = new Map<string, Promise<void>>();

function prefetchTaskKey(context: EngineCacheContext, conversationId: string): string {
    return `${context.storeGeneration}:${context.keyPrefix ?? "no-tenant"}:${conversationId}`;
}

async function fetchAndCacheConversation(
    cacheContext: EngineCacheContext,
    conversationId: string,
    reportLease: EngineSessionLease | null,
): Promise<void> {
    const lease = reportLease ?? Object.freeze({ instanceId: "cache-only", sessionGeneration: 0 });
    const adapter = createAdapterRuntime(lease, cacheContext, {
        reportData: Boolean(reportLease),
        suppressFailures: !reportLease,
    });
    let conversation: EngineConversation | null = null;
    const capture = adapter.runtime.subscribe(() => {
        const selected = adapter.runtime.getState().selectedConversation;
        if (selected) conversation = toEngineConversation(selected);
    });
    const loaded = await adapter.runtime.load(`prefetch:${conversationId}`, conversationId);
    capture();
    adapter.unsubscribe();
    if (!loaded || !conversation) throw new Error("Failed to cache conversation");
    if (!reportLease) {
        await writeEngineCache(cacheContext, `conversation:${conversationId}`, conversation);
    }
}

async function fetchAndCacheConversationCoalesced(
    cacheContext: EngineCacheContext,
    conversationId: string,
): Promise<void> {
    const taskKey = prefetchTaskKey(cacheContext, conversationId);
    const existing = prefetchInFlight.get(taskKey);
    if (existing) return existing;
    const task = fetchAndCacheConversation(cacheContext, conversationId, null);
    prefetchInFlight.set(taskKey, task);
    try {
        await task;
    } finally {
        if (prefetchInFlight.get(taskKey) === task) prefetchInFlight.delete(taskKey);
    }
}

async function prefetchAllConversations(
    cacheContext: EngineCacheContext,
    summaries: EngineConversationSummary[],
    refreshExisting = false,
): Promise<void> {
    const targets: string[] = [];
    for (const summary of summaries) {
        const taskKey = prefetchTaskKey(cacheContext, summary.id);
        if (
            refreshExisting ||
            prefetchInFlight.has(taskKey) ||
            !(await readEngineCache<EngineConversation>(cacheContext, `conversation:${summary.id}`))
        ) {
            targets.push(summary.id);
        }
    }

    let nextIndex = 0;
    const worker = async () => {
        while (nextIndex < targets.length) {
            const conversationId = targets[nextIndex++];
            try {
                await fetchAndCacheConversationCoalesced(cacheContext, conversationId);
            } catch (error) {
                if (refreshExisting) throw error;
            }
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(PREFETCH_CONVERSATION_CONCURRENCY, targets.length) }, worker),
    );
}

async function refreshConversationCacheAfterRun(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    conversationId: string,
): Promise<void> {
    if (!conversationId) return;
    try {
        await fetchAndCacheConversation(cacheContext, conversationId, lease);
        const adapter = createAdapterRuntime(lease, cacheContext, {
            reportData: true,
            suppressFailures: true,
        });
        await adapter.runtime.list(`refresh:${conversationId}`);
        adapter.unsubscribe();
    } catch {
        // The terminal stream snapshot is already visible. A later list/load retries.
    }
}

let offlineConversationCacheTask: {
    context: EngineCacheContext;
    promise: Promise<void>;
} | null = null;

async function handleCacheOfflineConversations(
    lease: EngineSessionLease,
    cacheContext: EngineCacheContext,
    commandId: string,
): Promise<void> {
    if (
        !offlineConversationCacheTask ||
        !sameEngineCacheContext(offlineConversationCacheTask.context, cacheContext) ||
        !prefs().isGenerationCurrent(offlineConversationCacheTask.context.storeGeneration)
    ) {
        const promise = (async () => {
            const adapter = createAdapterRuntime(lease, cacheContext, {
                reportData: true,
                suppressFailures: false,
            });
            const listed = await adapter.runtime.list(commandId);
            const summaries = adapter.runtime.getState().conversations;
            adapter.unsubscribe();
            if (!listed) throw new Error("Failed to cache conversation list");
            await prefetchAllConversations(cacheContext, summaries, true);
        })().finally(() => {
            if (offlineConversationCacheTask?.promise === promise) {
                offlineConversationCacheTask = null;
            }
        });
        offlineConversationCacheTask = { context: cacheContext, promise };
    }

    try {
        await offlineConversationCacheTask.promise;
        await plugin.reportOfflineCacheComplete(leased(lease, { commandId }));
    } catch (error) {
        reportCommandFailure(lease, commandId, error);
    }
}

function handleCommand(command: EngineCommand): void {
    if (!acceptedEngineLease) {
        if (
            readyHandshakePending &&
            command.instanceId === engineInstanceId &&
            Number.isSafeInteger(command.sessionGeneration) &&
            command.sessionGeneration >= 0
        ) {
            commandsAwaitingReady.push(command);
        }
        return;
    }

    const lease = acceptedCommandLease(command);
    if (!lease) return;
    const cacheContext = captureEngineCacheContext();
    // Marvin opened: a new question's settings round trip happens now, not after it is asked.
    if (command.type === "listConversations" || command.type === "loadConversation") {
        warmFeatureSettings(cacheContext);
    }

    switch (command.type) {
        case "listConversations":
            void handleListConversations(lease, cacheContext, command.id);
            return;
        case "loadConversation":
            if (!command.conversationId) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("loadConversation requires conversationId"),
                );
                return;
            }
            void handleLoadConversation(
                lease,
                cacheContext,
                command.id,
                command.conversationId,
                command.refresh === true,
            );
            return;
        case "cacheOfflineConversations":
            void handleCacheOfflineConversations(lease, cacheContext, command.id);
            return;
        case "send":
            if (!command.conversationId || command.text === undefined) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("send requires conversationId and text"),
                );
                return;
            }
            void handleSend(
                lease,
                cacheContext,
                command.id,
                command.conversationId,
                command.text,
                command.photos,
                depthOf(command),
                answeredQuestionOf(command.answersQuestion),
            );
            return;
        case "createAndRun":
            if (command.text === undefined) {
                reportCommandFailure(lease, command.id, new Error("createAndRun requires text"));
                return;
            }
            void handleCreateAndRun(
                lease,
                cacheContext,
                command.id,
                command.text,
                command.photos,
                command.sources,
                depthOf(command),
            );
            return;
        case "attach":
            if (!command.conversationId || !command.runId) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("attach requires conversationId and runId"),
                );
                return;
            }
            handleAttach(
                lease,
                cacheContext,
                command.id,
                command.conversationId,
                command.runId,
                command.text ?? "",
            );
            return;
        case "cancel":
            if (!command.conversationId) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("cancel requires conversationId"),
                );
                return;
            }
            handleCancel(command.conversationId);
            return;
        case "decideAction": {
            const { runId, actionId, approve } = command;
            if (!runId || !actionId || typeof approve !== "boolean") {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("decideAction requires runId, actionId and approve"),
                );
                return;
            }
            void reportCommandResult(lease, command.id, () =>
                decideAction(runId, actionId, approve),
            );
            return;
        }
        case "readMarvinShapes": {
            const { markup } = command;
            if (!isMarkupCommand(markup)) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error("readMarvinShapes requires markup.fileId and markup.page"),
                );
                return;
            }
            void reportCommandResult(lease, command.id, () => readMarvinShapes(markup));
            return;
        }
        case "decideMarvinShapes": {
            const { markup } = command;
            const shapeIds = markup?.shapeIds;
            const decision = markup?.decision;
            if (
                !isMarkupCommand(markup) ||
                !Array.isArray(shapeIds) ||
                shapeIds.length === 0 ||
                (decision !== "keep" && decision !== "remove")
            ) {
                reportCommandFailure(
                    lease,
                    command.id,
                    new Error(
                        "decideMarvinShapes requires markup.fileId, page, shapeIds and decision",
                    ),
                );
                return;
            }
            void reportCommandResult(lease, command.id, () =>
                decideMarvinShapes(markup, shapeIds, decision),
            );
            return;
        }
        default:
            reportCommandFailure(
                lease,
                command.id,
                new Error(`Unknown command type "${command.type}"`),
            );
    }
}

let initialized = false;
let commandListenerReady: Promise<unknown> | null = null;
let readyRevision = 0;

/** Legacy boot remains once per document. Canonical admission renews this
 * existing engine with a nonce produced by the successful native scope install. */
async function acquireEngineLease(admissionToken?: string): Promise<void> {
    const revision = ++readyRevision;
    const instanceId = engineInstanceId;
    readyHandshakePending = true;
    try {
        commandListenerReady ??= plugin.addListener("command", handleCommand);
        await commandListenerReady;
        if (revision !== readyRevision) return;
        const response = await plugin.reportReady({
            instanceId,
            ...(admissionToken ? { admissionToken } : {}),
        });
        if (revision !== readyRevision) return;
        const generation = response?.sessionGeneration;
        if (
            response?.instanceId !== instanceId ||
            !Number.isSafeInteger(generation) ||
            generation < 0
        ) {
            throw new Error("ConversationEngine reportReady returned an invalid session lease");
        }
        acceptedEngineLease = Object.freeze({
            instanceId: response.instanceId,
            sessionGeneration: generation,
        });
        readyHandshakePending = false;
        const queued = commandsAwaitingReady.splice(0);
        for (const command of queued) handleCommand(command);
    } catch (error) {
        if (revision === readyRevision) {
            readyHandshakePending = false;
            commandsAwaitingReady.splice(0);
        }
        throw error;
    }
}

let resumesOnForeground = false;

/** Reopens every in-flight run's stream when the app returns to the foreground. While
 * iOS suspends the app, its stream can die without an error, which would leave the
 * question "thinking" after the answer was saved. */
function resumeRunsOnForeground(): void {
    if (resumesOnForeground) return;
    resumesOnForeground = true;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        activeRuns.forEach((run) => run.runtime.resume());
    });
}

export function initConversationEngine(): void {
    if (initialized) return;
    initialized = true;
    resumeRunsOnForeground();
    // acquireEngineLease owns the handshake for both the first init and a
    // rebind after a tenant switch: it registers the command listener once and
    // guards against a concurrent rebind superseding this attempt.
    const admissionToken = (
        globalThis as typeof globalThis & {
            __primepointBridgeAdmissionToken?: unknown;
        }
    ).__primepointBridgeAdmissionToken;
    void acquireEngineLease(typeof admissionToken === "string" ? admissionToken : undefined).catch(
        (error) =>
            console.error("ConversationEngine: failed to acquire native session lease", error),
    );
}

export async function rebindConversationEngine(admissionToken: string): Promise<void> {
    acceptedEngineLease = null;
    commandsAwaitingReady.splice(0);
    // Same teardown as handleCancel, for every run at once: mark it settled so
    // no late stream event reports against the retired lease, then cancel and
    // unsubscribe.
    activeRuns.forEach((run) => {
        run.settled = true;
        void run.runtime.cancel().finally(() => run.unsubscribe());
    });
    activeRuns.clear();
    engineInstanceId = createEngineInstanceId();
    initialized = true;
    resumeRunsOnForeground();
    await acquireEngineLease(admissionToken);
}
